import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    Grid,
    Badge,
    StylelessButton,
} from  'ui-kit';
import { Tooltip } from 'app/react/components/_index';
import {
    DropTarget as dropTarget,
    DragSource as dragSource,
} from 'react-dnd';
import './Item.module.css';

const { Cell } = Grid;
const Types = {
    ROW: 'pageRow'
};

const HOME_PAGE_LABEL = 'HOMEPAGE';
const FORM_PAGE_LABEL = 'FORM PAGE';
const DROP_CELL_WIDTH = '50px';
const NAME_CELL_WIDTH = '400px';
const ACTION_CELL_WIDTH = '150px';

const TOOLTIPS = {
    UPDATE: 'Edit page',
    DELETE: 'Delete page',
    UPDATE_FORM_PAGE: 'Form page setting'
};

const ChildPageItem = ({
    id,
    parentId,
    name,
    slug,
    isHomePage = false,
    isFormPage = false,
    isOverTopArea = false,
    isOverBottomArea = false,
    onUpdate = () => {},
    onDelete = () => {},
    updateHoverState = () => {},
    updatePageSequence = () => {},
    dropOutsideList = () => {},
    // Drag props
    isDragging = false,
    isOver = false,
    canDrop = false,
    connectDragSource = () => {},
    connectDropTarget = () => {}
}) => (
    connectDragSource(
        connectDropTarget(
            <li
                styleName={classNames('item', {
                    isDragging,
                    dragOnTop: isOverTopArea,
                    dragOnBottom: isOverBottomArea,
                })}
            >
                <div styleName="itemContent">
                    <Grid hasGutters={false}>
                        <Cell isAutoSized width={DROP_CELL_WIDTH}>
                            <i className="bs-icon-cursor-move" />
                        </Cell>
                        <Cell isAutoSized width={NAME_CELL_WIDTH}>
                            <span className="margin-tiny-right">{name}</span>
                            {isHomePage && (
                                <Badge>{HOME_PAGE_LABEL}</Badge>
                            )}
                            {isFormPage && (
                                <Badge type={Badge.SUCCESS}>{FORM_PAGE_LABEL}</Badge>
                            )}
                        </Cell>
                        <Cell>
                            <code>{slug}</code>
                        </Cell>
                        <Cell
                            isAutoSized
                            width={ACTION_CELL_WIDTH}
                            style={{ textAlign: 'right', marginRight: 'var(--spacingMedium)' }}
                        >
                            <Tooltip content={TOOLTIPS.UPDATE}>
                                <StylelessButton onClick={() => onUpdate(id, parentId)}>
                                    <i className="bs-icon-pencil" />
                                </StylelessButton>
                            </Tooltip>
                            {!isFormPage && !isHomePage && (
                                <Tooltip content={TOOLTIPS.DELETE}>
                                    <StylelessButton onClick={() => onDelete(id, parentId)}>
                                        <i className="bs-icon-bin" />
                                    </StylelessButton>
                                </Tooltip>
                            )}
                        </Cell>
                    </Grid>
                </div>
            </li>
        )
    )
);


ChildPageItem.propTypes = {
    id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]).isRequired,
    parentId: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    isHomePage: PropTypes.bool,
    isFormPage: PropTypes.bool,
    isOverTopArea: PropTypes.bool,
    isOverBottomArea: PropTypes.bool,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    updatePageSequence: PropTypes.func,
    updateHoverState: PropTypes.func,
    dropOutsideList: PropTypes.func,
    isDragging: PropTypes.bool,
    isOver: PropTypes.bool,
    canDrop: PropTypes.bool,
    connectDragSource: PropTypes.func
};

const sourceSpec = {
    beginDrag: (props) => ({
        id: props.id,
        parentId: props.parentId,
        sequence: props.sequence,
        name: props.name,
        isFormPage: props.isFormPage
    }),
    endDrag: (props, monitor) => {
        if (monitor.getDropResult() === null) {
            props.dropOutsideList(props.id, props.parentId);
        }
    }
};

const targetSpec = {
    drop: (props, monitor, component) => {
        const dragItem = monitor.getItem();
        if (dragItem) {
            // Don't replace items with themselves
            if (dragItem.id === props.id) return;
            props.updatePageSequence(
                dragItem.id,
                props.id,
                dragItem.parentId,
                props.parentId,
                props.isOverTopArea,
                false,
                props.isOverBottomArea
            );
        }
    },
    hover: (props, monitor, component) => {
        const dragItem = monitor.getItem();
        // Don't replace items with themselves
        if (dragItem.id === props.id) {
            return;
        }
        // Determine rectangle on screen
        const hoverBoundingRect = ReactDOM.findDOMNode(component).getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // Determine mouse position
        const clientOffset = monitor.getClientOffset();
        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        const isOverTopArea = hoverClientY <= hoverMiddleY;
        const isOverBottomArea = hoverClientY > hoverMiddleY;
        // Is cursor is still over same area don't dispatch
        const isStillOverTop = props.isOverTopArea && isOverTopArea;
        const isStillOverBottom = props.isOverBottomArea && isOverBottomArea;
        if (isStillOverTop || isStillOverBottom) {
            return;
        }
        if (dragItem.hasChildren) {
            return;
        }
        props.updateHoverState(
            dragItem.id,
            props.id,
            dragItem.parentId,
            props.parentId,
            isOverTopArea,
            false,
            isOverBottomArea
        );
    }
};

const collectSourceProps = (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
});

const collectTargetProps = (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    isOverTop: () => monitor.isOver(),
    isOverCurrent: monitor.isOver({ shallow: true }),
    canDrop: monitor.canDrop()
});

export default dropTarget(
    Types.ROW, targetSpec, collectTargetProps
)(dragSource(
    Types.ROW, sourceSpec, collectSourceProps
)(ChildPageItem));
